import { useMount } from 'react-use';
import { useCallback, useMemo } from 'react';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { commonTranslationKeys, createTranslatedText } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import {
   getDefaultDosageUnit,
   getSortedPreciseDosageUnits,
   isDosageUnitEditable,
   PreciseDosageUnit,
   setPreferredDosageUnit,
} from '~libs.shared.prescription';
import { MedicineSinglePackageUnit } from '~libs.shared.medicine';

import {
   DraftPrescriptionFieldName,
   DraftPrescriptionsFormController,
} from '../../../draftPrescriptions/DraftPrescriptionsForm.types';

import { StyledDosageUnitsSelect } from './DosageUnitsSelect.styled';

const DOSAGE_UNITS_SELECT_WIDTH = '92px';

const triggerLabelExtractor = (selectedItem: string): string => selectedItem;

type DosageUnitsSelectProps = Pick<DraftPrescriptionsFormController, 'register' | 'setValue'> & {
   readonly name: DraftPrescriptionFieldName;
   readonly defaultValue: PreciseDosageUnit | null;
   readonly medicine: MedicineSimple;
   readonly required: boolean;
};

export const DosageUnitsSelect = (props: DosageUnitsSelectProps): JSX.Element => {
   const items = useMemo(() => {
      return getSortedPreciseDosageUnits(props.medicine);
   }, [props.medicine]);

   const defaultDosageUnit = props.defaultValue ?? getDefaultDosageUnit(props.medicine);

   const editable = isDosageUnitEditable(props.medicine);

   useMount(() => {
      if (!props.defaultValue && defaultDosageUnit) {
         props.setValue(props.name, defaultDosageUnit);
      }
   });

   const handleItemSelect = useCallback(
      (item: PreciseDosageUnit | MedicineSinglePackageUnit | null) => {
         if (item) {
            setPreferredDosageUnit(props.medicine, item);
         }
      },
      [props.medicine]
   );

   return (
      <StyledDosageUnitsSelect<PreciseDosageUnit | MedicineSinglePackageUnit>
         triggerLabelExtractor={triggerLabelExtractor}
         testId={'dosageUnit'}
         placeholder={commonTranslationKeys.choose}
         controller={props.register(props.name, {
            required: props.required,
         })}
         defaultValue={defaultDosageUnit}
         onSelect={handleItemSelect}
         width={DOSAGE_UNITS_SELECT_WIDTH}
         disabled={!editable}
         compact
      >
         <Select.List<string> items={items}>
            {(unit: string) => <Select.Item item={unit} testId={unit} key={unit} label={createTranslatedText(unit)} />}
         </Select.List>
      </StyledDosageUnitsSelect>
   );
};
